import { useState, useEffect } from 'react'
import { X, Bug, Sparkles, MessageSquare, Loader2, CheckCircle2, AlertCircle } from 'lucide-react'
import clsx from 'clsx'

interface FeedbackModalProps {
  isOpen: boolean
  onClose: () => void
  initialType?: 'bug' | 'feature' | 'feedback'
}

type FeedbackType = 'bug' | 'feature' | 'feedback'

interface FormData {
  type: FeedbackType
  title: string
  description: string
}

interface SubmitState {
  isSubmitting: boolean
  success: boolean
  error: string | null
  issueNumber?: number
  issueUrl?: string
}

export default function FeedbackModal({ isOpen, onClose, initialType = 'bug' }: FeedbackModalProps) {
  const [formData, setFormData] = useState<FormData>({
    type: initialType,
    title: '',
    description: ''
  })

  const [submitState, setSubmitState] = useState<SubmitState>({
    isSubmitting: false,
    success: false,
    error: null
  })

  const [validationErrors, setValidationErrors] = useState<{
    title?: string
    description?: string
  }>({})

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setFormData({
        type: initialType,
        title: '',
        description: ''
      })
      setSubmitState({
        isSubmitting: false,
        success: false,
        error: null
      })
      setValidationErrors({})
    }
  }, [isOpen, initialType])

  // Keyboard shortcuts
  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (e: KeyboardEvent) => {
      // Escape to close
      if (e.key === 'Escape' && !submitState.isSubmitting) {
        onClose()
      }

      // Cmd/Ctrl + Enter to submit
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && !submitState.isSubmitting && !submitState.success) {
        e.preventDefault()
        const form = document.querySelector('form')
        if (form) form.requestSubmit()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen, submitState.isSubmitting, submitState.success, onClose])

  const validateForm = (): boolean => {
    const errors: { title?: string; description?: string } = {}

    if (formData.title.trim().length < 3) {
      errors.title = 'title must be at least 3 characters'
    } else if (formData.title.trim().length > 100) {
      errors.title = 'title must be 100 characters or less'
    }

    if (formData.description.trim().length < 10) {
      errors.description = 'description must be at least 10 characters'
    } else if (formData.description.trim().length > 2000) {
      errors.description = 'description must be 2000 characters or less'
    }

    setValidationErrors(errors)
    return Object.keys(errors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!validateForm()) {
      return
    }

    setSubmitState({
      isSubmitting: true,
      success: false,
      error: null
    })

    try {
      // Submit to n8n webhook
      const webhookUrl = import.meta.env.VITE_N8N_WEBHOOK_URL || 'https://n8n.ozdust.me/webhook/support-help'
      const response = await fetch(webhookUrl.replace('/support-help', '/feedback'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          type: formData.type,
          title: formData.title.trim(),
          description: formData.description.trim(),
          source: 'shellby-help',
          userAgent: navigator.userAgent,
          url: window.location.href,
          timestamp: new Date().toISOString()
        })
      })

      if (!response.ok) {
        throw new Error('Failed to submit feedback')
      }

      const data = await response.json()

      setSubmitState({
        isSubmitting: false,
        success: true,
        error: null,
        issueNumber: data.issueNumber,
        issueUrl: data.url
      })

      // Auto-close after 3 seconds on success
      setTimeout(() => {
        onClose()
      }, 3000)
    } catch (error) {
      setSubmitState({
        isSubmitting: false,
        success: false,
        error: error instanceof Error ? error.message : 'Failed to submit feedback'
      })
    }
  }

  const handleRetry = () => {
    setSubmitState({
      isSubmitting: false,
      success: false,
      error: null
    })
  }

  if (!isOpen) return null

  const typeOptions: Array<{ value: FeedbackType; label: string; icon: React.ReactNode; color: string }> = [
    {
      value: 'bug',
      label: 'bug report',
      icon: <Bug className="h-5 w-5" strokeWidth={1.5} />,
      color: 'from-[var(--bbs-synthwave-hot-pink)] to-[var(--bbs-synthwave-cyan)]'
    },
    {
      value: 'feature',
      label: 'feature request',
      icon: <Sparkles className="h-5 w-5" strokeWidth={1.5} />,
      color: 'from-[var(--bbs-jadeite-electric)] to-[var(--bbs-color-success-accent)]'
    },
    {
      value: 'feedback',
      label: 'general feedback',
      icon: <MessageSquare className="h-5 w-5" strokeWidth={1.5} />,
      color: 'from-[var(--bbs-jadeite-electric)] to-[var(--bbs-synthwave-cyan)]'
    }
  ]

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4 animate-fadeIn"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitState.isSubmitting) {
          onClose()
        }
      }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-md" />

      {/* Modal */}
      <div className="card relative w-full max-w-2xl max-h-[90vh] overflow-y-auto" style={{ borderRadius: 'var(--bbs-radius-lg)' }} data-static-card>
        {/* Header */}
        <div className="sticky top-0 z-10 glass-texture-medium border-b border-white/10" style={{ padding: 'var(--bbs-spacing-lg) var(--bbs-spacing-xl)' }}>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-medium text-[var(--bbs-text-primary)]">
              submit feedback
            </h2>
            <button
              onClick={onClose}
              disabled={submitState.isSubmitting}
              className="p-2 rounded-lg hover:bg-white/5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Close feedback modal"
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div style={{ padding: 'var(--bbs-spacing-xl)' }}>
          {submitState.success ? (
            // Success State
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--bbs-jadeite-electric)]/20 mb-4">
                <CheckCircle2 className="h-8 w-8 text-[var(--bbs-jadeite-electric)]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-semibold text-[var(--bbs-text-primary)] mb-2">
                feedback submitted!
              </h3>
              <p className="text-sm text-[var(--bbs-text-secondary)] mb-4">
                {submitState.issueNumber
                  ? `your feedback has been recorded as issue #${submitState.issueNumber}`
                  : 'thank you for your feedback!'}
              </p>
              {submitState.issueUrl && (
                <a
                  href={submitState.issueUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[var(--bbs-jadeite-electric)] hover:underline transition-colors"
                >
                  view on github →
                </a>
              )}
              <p className="text-xs text-[var(--bbs-text-tertiary)] mt-6">
                closing in 3 seconds...
              </p>
            </div>
          ) : submitState.error ? (
            // Error State
            <div className="text-center py-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[var(--bbs-synthwave-hot-pink)]/20 mb-4">
                <AlertCircle className="h-8 w-8 text-[var(--bbs-synthwave-hot-pink)]" strokeWidth={1.5} />
              </div>
              <h3 className="text-lg font-medium text-[var(--bbs-text-primary)] mb-2">
                submission failed
              </h3>
              <p className="text-sm text-[var(--bbs-text-secondary)] mb-6">
                {submitState.error}
              </p>
              <button
                onClick={handleRetry}
                className="font-medium bg-[var(--bbs-jadeite-electric)] text-[var(--bbs-color-background)] hover:opacity-90 transition-opacity"
                style={{
                  padding: 'var(--bbs-spacing-md) var(--bbs-spacing-xl)',
                  borderRadius: 'var(--bbs-radius-md)'
                }}
              >
                try again
              </button>
            </div>
          ) : (
            // Form State
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Type Selector */}
              <div>
                <label className="block text-xs font-medium text-[var(--bbs-text-secondary)] mb-3 uppercase tracking-wide">
                  feedback type
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {typeOptions.map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setFormData({ ...formData, type: option.value })}
                      className={clsx(
                        'relative border transition-all duration-300',
                        formData.type === option.value
                          ? 'border-[var(--bbs-jadeite-electric)] bg-[var(--bbs-jadeite-electric)]/10 shadow-[0_0_15px_rgba(4,255,178,0.2)]'
                          : 'border-white/10 hover:border-white/20 bg-white/5'
                      )}
                      style={{
                        padding: 'var(--bbs-spacing-md)',
                        borderRadius: 'var(--bbs-radius-md)'
                      }}
                    >
                      <div className="flex items-center gap-3">
                        <div className={clsx(
                          'transition-colors duration-300',
                          formData.type === option.value ? 'text-[var(--bbs-jadeite-electric)]' : 'text-[var(--bbs-text-tertiary)]'
                        )}>
                          {option.icon}
                        </div>
                        <span className="text-sm font-medium text-[var(--bbs-text-primary)]">
                          {option.label}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Title Input */}
              <div>
                <label htmlFor="feedback-title" className="block text-xs font-medium text-[var(--bbs-text-secondary)] mb-2 uppercase tracking-wide">
                  title <span className="text-[var(--bbs-synthwave-hot-pink)]">*</span>
                </label>
                <input
                  id="feedback-title"
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="brief summary of your feedback..."
                  maxLength={100}
                  className={clsx(
                    'w-full bg-white/5 border text-[var(--bbs-text-primary)] placeholder:text-[var(--bbs-text-quaternary)] focus:outline-none focus:ring-2 focus:ring-[var(--bbs-jadeite-electric)] transition-all',
                    validationErrors.title ? 'border-[var(--bbs-synthwave-hot-pink)]/50' : 'border-white/10'
                  )}
                  style={{
                    padding: 'var(--bbs-spacing-md) var(--bbs-spacing-lg)',
                    borderRadius: 'var(--bbs-radius-md)'
                  }}
                  disabled={submitState.isSubmitting}
                />
                {validationErrors.title && (
                  <p className="mt-1 text-xs text-[var(--bbs-synthwave-hot-pink)]">
                    {validationErrors.title}
                  </p>
                )}
                <p className="mt-1 text-xs text-[var(--bbs-text-quaternary)]">
                  {formData.title.length}/100 characters
                </p>
              </div>

              {/* Description Textarea */}
              <div>
                <label htmlFor="feedback-description" className="block text-xs font-medium text-[var(--bbs-text-secondary)] mb-2 uppercase tracking-wide">
                  description <span className="text-[var(--bbs-synthwave-hot-pink)]">*</span>
                </label>
                <textarea
                  id="feedback-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="please provide details about your feedback..."
                  rows={6}
                  maxLength={2000}
                  className={clsx(
                    'w-full bg-white/5 border text-[var(--bbs-text-primary)] placeholder:text-[var(--bbs-text-quaternary)] focus:outline-none focus:ring-2 focus:ring-[var(--bbs-jadeite-electric)] transition-all resize-none',
                    validationErrors.description ? 'border-[var(--bbs-synthwave-hot-pink)]/50' : 'border-white/10'
                  )}
                  style={{
                    padding: 'var(--bbs-spacing-md) var(--bbs-spacing-lg)',
                    borderRadius: 'var(--bbs-radius-md)'
                  }}
                  disabled={submitState.isSubmitting}
                />
                {validationErrors.description && (
                  <p className="mt-1 text-xs text-[var(--bbs-synthwave-hot-pink)]">
                    {validationErrors.description}
                  </p>
                )}
                <p className="mt-1 text-xs text-[var(--bbs-text-quaternary)]">
                  {formData.description.length}/2000 characters
                </p>
              </div>

              {/* Submit Button */}
              <div className="flex items-center justify-between pt-4 border-t border-white/10">
                <p className="text-xs text-[var(--bbs-text-tertiary)]">
                  <kbd className="px-2 py-1 rounded bg-white/10 text-[var(--bbs-text-secondary)]">Cmd</kbd> + <kbd className="px-2 py-1 rounded bg-white/10 text-[var(--bbs-text-secondary)]">Enter</kbd> to submit
                </p>
                <button
                  type="submit"
                  disabled={submitState.isSubmitting}
                  className="relative font-medium overflow-hidden transition-all duration-300 min-w-[140px] disabled:opacity-50 bg-[var(--bbs-jadeite-electric)] text-[var(--bbs-color-background)] hover:shadow-[0_0_20px_rgba(4,255,178,0.4)]"
                  style={{
                    padding: 'var(--bbs-spacing-md) var(--bbs-spacing-xl)',
                    borderRadius: 'var(--bbs-radius-md)'
                  }}
                >
                  <span className="relative z-10 flex items-center justify-center gap-2">
                    {submitState.isSubmitting ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" strokeWidth={2} />
                        <span>submitting...</span>
                      </>
                    ) : (
                      'submit feedback'
                    )}
                  </span>
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  )
}
